import { forwardRef } from 'react';
import cn from '../../utils/cn';
import KoobSidebarBadge from './KoobSidebarBadge';
import { useRouterContext } from './routerContext';

const KoobSidebarFlyout = forwardRef<HTMLDivElement, any>(
  function KoobSidebarFlyout(
    { parentLabel, childLinks = [], onMouseEnter, onMouseLeave },
    ref,
  ) {
    const { NavLink } = useRouterContext();

    if (!childLinks.length) return null;

    return (
      <div
        ref={ref}
        className="fixed z-popover"
        style={{ pointerEvents: 'auto' }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* Invisible bridge covering the gap between sidebar and flyout */}
        <div
          className="absolute top-0 bottom-0 right-full"
          style={{ width: 8 }}
        />
        <style>{`
        .koob-flyout-link.active {
          background: linear-gradient(to bottom, #F68B11, #F67008);
          color: white;
        }
      `}</style>
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[180px]">
          <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            {parentLabel}
          </div>
          {childLinks.map((child, index) =>
            child.disabled ? (
              <div
                key={index}
                className={cn(
                  'flex items-center justify-between gap-2 px-3 py-2 text-sm text-gray-300 select-none',
                  child.onDisabledClick
                    ? 'cursor-pointer'
                    : 'cursor-not-allowed',
                )}
                title={child.disabledTip}
                onClick={child.onDisabledClick}
                data-testid={child.dataCy}
              >
                <div className="flex items-center gap-2">
                  {child.icon && (
                    <i className={cn(child.icon, 'fa-fw text-sm shrink-0')} />
                  )}
                  <span className="truncate">{child.label}</span>
                </div>
              </div>
            ) : !child.to ? (
              // Action child (opens the trip wizard over the page), not a route.
              <button
                key={index}
                onClick={child.onClick}
                data-testid={child.dataCy}
                className="koob-flyout-link flex w-full items-center gap-2 border-none bg-transparent px-3 py-2 text-left text-sm text-gray-600 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-900"
              >
                {child.icon && (
                  <i className={cn(child.icon, 'fa-fw text-sm shrink-0')} />
                )}
                <span className="truncate">{child.label}</span>
              </button>
            ) : (
              <NavLink
                key={index}
                to={child.to}
                end={child.end}
                className={cn(
                  'koob-flyout-link flex items-center justify-between gap-2 px-3 py-2 text-sm transition-colors duration-150 text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                )}
                onClick={child.onClick}
                data-testid={child.dataCy}
              >
                <div className="flex items-center gap-2">
                  {child.icon && (
                    <i className={cn(child.icon, 'fa-fw text-sm shrink-0')} />
                  )}
                  <span className="truncate">{child.label}</span>
                </div>
                {child.badge != null && child.badge > 0 && (
                  <KoobSidebarBadge count={child.badge} />
                )}
              </NavLink>
            ),
          )}
        </div>
      </div>
    );
  },
);

export default KoobSidebarFlyout;
